/**
 * Daily Special Component
 * Displays daily special if available.
 */

import { useState } from 'react';
import { useTodaysMenu } from '../../hooks/useMenu';
import { ItemOptionsPicker } from './ItemOptionsPicker';

interface DailySpecialProps {
  onOrder?: (specialId: number, specialName: string, optionIds?: number[], optionNames?: string[]) => void;
  disabled?: boolean;
}

export function DailySpecial({ onOrder, disabled = false }: DailySpecialProps) {
  const { menu, loading, error } = useTodaysMenu();
  const [selectedBySpecial, setSelectedBySpecial] = useState<Record<number, number[]>>({});

  // Use daily_specials array if available, otherwise fall back to single daily_special
  const specialsArray = menu?.daily_specials || (menu?.daily_special ? [menu.daily_special] : []);

  // Filter out specials that replace the lunch menu (replacement_mode === 'replace_lunch')
  const displayableSpecials = specialsArray.filter(special => {
    const mode = special.replacement_mode || (special.replaces_regular ? 'replace_lunch' : 'add');
    return mode !== 'replace_lunch';
  });

  const selectedFor = (id?: number) => (id ? selectedBySpecial[id] ?? [] : []);
  const needsPick = (special: typeof displayableSpecials[0]) =>
    !!special.item_options && special.item_options.options.length > 0;
  const pickMissing = (special: typeof displayableSpecials[0]) =>
    needsPick(special) && selectedFor(special.id).length === 0;

  const handleOrderClick = (special: typeof displayableSpecials[0]) => {
    if (disabled) return;
    if (special && special.id && onOrder) {
      const picked = selectedFor(special.id);
      const names = special.item_options
        ? special.item_options.options.filter((o) => picked.includes(o.id)).map((o) => o.name)
        : [];
      onOrder(special.id, special.name,
              needsPick(special) ? picked : undefined,
              needsPick(special) ? names : undefined);
    }
  };

  const renderPicker = (special: typeof displayableSpecials[0]) =>
    needsPick(special) && special.id && special.item_options ? (
      <ItemOptionsPicker
        itemOptions={special.item_options}
        selected={selectedFor(special.id)}
        onChange={(ids) => setSelectedBySpecial((prev) => ({ ...prev, [special.id as number]: ids }))}
        disabled={disabled}
      />
    ) : null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-hmh-blue px-4 py-3">
          <h3 className="text-lg font-bold text-white">Special</h3>
        </div>
        <div className="p-6 flex-grow">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-hmh-blue px-4 py-3">
          <h3 className="text-lg font-bold text-white">Special</h3>
        </div>
        <div className="p-6 flex-grow">
          <p className="text-gray-500 text-sm">Unable to load special</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
      {/* Blue header banner */}
      <div className="bg-hmh-blue px-4 py-3">
        <h3 className="text-lg font-bold text-white">Special{displayableSpecials.length > 1 ? 's' : ''}</h3>
      </div>

      {/* Card content with padding */}
      <div className="p-6 flex-grow flex flex-col">
        {displayableSpecials.length > 0 ? (
          displayableSpecials.length === 1 ? (
            // Single special - push button to bottom
            <>
              <div className="flex-grow">
                <p className="text-xl font-bold text-gray-900 mb-2">{displayableSpecials[0].name}</p>
                {displayableSpecials[0].description && (
                  <p className="text-gray-500 text-base">{displayableSpecials[0].description}</p>
                )}
                {renderPicker(displayableSpecials[0])}
              </div>
              <button
                onClick={() => handleOrderClick(displayableSpecials[0])}
                disabled={disabled || !displayableSpecials[0]?.id || pickMissing(displayableSpecials[0])}
                className={`w-full py-3 px-4 rounded-lg text-base font-medium transition-colors mt-4 ${
                  disabled || !displayableSpecials[0]?.id || pickMissing(displayableSpecials[0])
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-hmh-blue text-white hover:bg-hmh-dark'
                }`}
              >
                {pickMissing(displayableSpecials[0]) ? 'Select an option above' : 'Add to Order'}
              </button>
            </>
          ) : (
            // Multiple specials - each has its own button
            <div className="flex-grow space-y-4">
              {displayableSpecials.map((special, index) => (
                <div key={special.id || index} className={index > 0 ? "pt-4 border-t border-gray-200" : ""}>
                  <p className="text-xl font-bold text-gray-900 mb-2">{special.name}</p>
                  {special.description && (
                    <p className="text-gray-500 text-base mb-4">{special.description}</p>
                  )}
                  {renderPicker(special)}
                  <button
                    onClick={() => handleOrderClick(special)}
                    disabled={disabled || !special?.id || pickMissing(special)}
                    className={`w-full py-3 px-4 rounded-lg text-base font-medium transition-colors ${
                      disabled || !special?.id || pickMissing(special)
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-hmh-blue text-white hover:bg-hmh-dark'
                    }`}
                  >
                    {pickMissing(special) ? 'Select an option above' : 'Add to Order'}
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          <p className="text-gray-500 text-base">No special today</p>
        )}
      </div>
    </div>
  );
}
